import { AuthChallenge } from './data/AuthChallenge';
import TokenServiceFactory from './TokenServiceFactory';
import {
	SecondaryTokenFetchFailure,
	AMLErrorCode,
	PrimaryTokenFetchFailure,
	GatewaySessionError,
	authErrorString,
} from '../AuthErrors';
import AuthUtils from '../AuthUtils';
import TokenManager from '../TokenManager';
import ctxsNative from '../ctxsNative';
import { CookieManager } from '../CookieManager';
import { Logger } from '../Logger';
import { basicHdrs, headerConstants } from '../AuthConstants';
import { logConstants } from '../../utils/Constants';
import {
	RequestParams,
	ResponseToNative,
	HTTPFetchResponse,
	HeadersJson,
	PrimaryTokenTableEntry,
	ProtectionMode,
	SecondaryTokenResponseToNative,
} from './data/AuthTypes';
import { TokenRequest } from './data/TokenRequest';
import Utils from '../../utils/Utils';
import { HttpErrorCodes, HttpRequestError } from '../../utils/http/HttpErrors';
import { SentryLogger } from '../../sentryLogging/SentryLogger';
import NativeBridgeWrapper from './NativeBridgeWrapper';
import { v4 as uuidv4 } from 'uuid';
import { HeaderParser } from './parser/HeaderParser';
import { CryptoParams } from '../main';
import DsAuthPrimaryTokenServiceBase from './DsAuthPrimaryTokenServiceBase';
import { HttpStatusCodes } from '../../utils/http/HttpConstants';

export default class DSAuth {
	private static instance: DSAuth;

	static getInstance(): DSAuth {
		if (!DSAuth.instance) {
			DSAuth.instance = new DSAuth();
		}
		return DSAuth.instance;
	}

	//Prepares the request for the token by replacing the realm, serviceroot-hint and tokenlifetime
	prepareTokenRequest(
		authProps: AuthChallenge,
		tokenLifetime: string,
		url: string,
		transactionId: string
	): TokenRequest {
		const serviceRootHint = authProps.getServicerootHint();
		if (!serviceRootHint || !url.includes(serviceRootHint)) {
			Logger.getInstance().write(
				logConstants.INFO,
				'Service root hint not matching request url, Hint: ' +
					serviceRootHint +
					' Url:' +
					Utils.getInstance().removeQueryStringAndHashFromUrl(url)
			);
		}
		url = Utils.getInstance().removeQueryStringAndHashFromUrl(url); // Removing query string and hash since token request fails otherwise
		const headers = {
			'Cache-Control': 'no-cache',
			'X-Citrix-IsUsingHTTPS': 'Yes',
			'Content-Type': 'application/vnd.citrix.requesttoken+xml',
			Accept: '*/*',
		};
		const reqHeaders = this.addBasicHeaders(headers, transactionId);
		const body = `<?xml version="1.0" encoding="UTF-8" standalone="no" ?><requesttoken xmlns="http://citrix.com/delivery-services/1-0/auth/requesttoken"><for-service>${authProps.getRealm()}</for-service><for-service-url>${url}</for-service-url><reqtokentemplate></reqtokentemplate><requested-lifetime>${tokenLifetime}</requested-lifetime></requesttoken>`;
		return new TokenRequest(reqHeaders, body);
	}

	//Prepares the request for destroying the token by putting the token in the request body
	prepareDestroyTokenRequest(token: string, id: string): TokenRequest {
		const headers = {
			'Cache-Control': 'no-cache',
			'X-Citrix-IsUsingHTTPS': 'Yes',
			'Content-Type': 'application/vnd.citrix.destroytoken+xml',
			Accept: '*/*',
		};
		const reqHeaders = this.addBasicHeaders(headers, id);
		const body = `<destroytoken xmlns="http://citrix.com/delivery-services/1-0/auth/destroytoken"><token>${token}</token></destroytoken>`;
		return new TokenRequest(reqHeaders, body);
	}

	//Adds the basic headers for DSAuth URL request if not supplied
	addBasicHeaders(headers: HeadersJson, transactionId: string): HeadersJson {
		const temp = { ...headers }; //Copying to a new object
		const basicHdrsWithTransactionId = {
			...basicHdrs,
			...{ [headerConstants.TRANSACTION_ID]: !!transactionId ? transactionId : uuidv4() },
		};
		for (const key in basicHdrsWithTransactionId) {
			//Copy the basic headers if not provided by native.
			const hdrValue = HeaderParser.getInstance().extractHeaderValue(headers, key);
			if (typeof hdrValue === 'undefined' || hdrValue === null) {
				temp[key] = basicHdrsWithTransactionId[key];
			}
		}
		return temp;
	}

	//Entry point to the start of the DS Auth URL request
	//starts the SecondaryToken flow
	async startUrlRequest(requestParams: RequestParams): Promise<ResponseToNative> {
		try {
			return await TokenServiceFactory.getSecondaryTokenServiceInstance(
				requestParams.referenceUri
			).getAuthResponseForURL(requestParams); //authresponse will be sent
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				requestParams.id + ':exception ' + JSON.stringify(e) + ':' + e.stack
			);
			const authUtils = AuthUtils.getInstance();
			if (e instanceof SecondaryTokenFetchFailure) {
				return authUtils.constructAMLResponse({
					id: requestParams.id,
					errorCode: AMLErrorCode.AML_SECONDARY_TOKEN_FETCH_FAIL,
					errorMessage: e.stack,
				});
			} else if (e instanceof PrimaryTokenFetchFailure) {
				return authUtils.constructAMLResponse({
					id: requestParams.id,
					errorCode: AMLErrorCode.AML_PRIMARY_TOKEN_FETCH_FAIL,
					errorMessage: e.stack,
				});
			} else if (e instanceof GatewaySessionError) {
				return authUtils.constructAMLResponse({
					id: requestParams.id,
					URLResponse: {
						statusCode: HttpStatusCodes.HTTP_STATUS_DENIED,
						body: '',
						headers: {},
					},
					errorCode: AMLErrorCode.AML_START_URL_REQUEST_EXCEPTION,
					errorMessage: authErrorString.GATEWAY_SESSION_EXPIRED_ALLOWCREDUI,
				});
			}
			SentryLogger.getInstance().logError(e, {
				tags: { url: requestParams.referenceUri },
				customMessage: 'startUrlRequest failed due to an error',
				additionalContext: {
					innerError: e.innerError,
				},
			});
			return authUtils.constructAMLResponse({
				id: requestParams.id,
				errorCode: AMLErrorCode.AML_START_URL_REQUEST_EXCEPTION,
				errorMessage: e.stack,
			});
		}
	}

	async getSecondaryTokenForUrl(
		requestParams: RequestParams
	): Promise<SecondaryTokenResponseToNative> {
		try {
			return await TokenServiceFactory.getSecondaryTokenServiceInstance(
				requestParams.referenceUri
			).getSecondaryTokenForUrlFromCacheOrNetwork(requestParams);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				requestParams.id + ':exception ' + JSON.stringify(e) + ':' + e.stack
			);
			const authUtils = AuthUtils.getInstance();
			if (e instanceof SecondaryTokenFetchFailure) {
				return authUtils.constructAMLSecondaryTokenResponseToNative({
					id: requestParams.id,
					errorCode: AMLErrorCode.AML_SECONDARY_TOKEN_FETCH_FAIL,
					errorMessage: e.stack,
				});
			} else if (e instanceof PrimaryTokenFetchFailure) {
				return authUtils.constructAMLSecondaryTokenResponseToNative({
					id: requestParams.id,
					errorCode: AMLErrorCode.AML_PRIMARY_TOKEN_FETCH_FAIL,
					errorMessage: e.stack,
				});
			}
			SentryLogger.getInstance().logError(e, {
				tags: { url: requestParams.referenceUri },
				customMessage: 'getSecondaryTokenForUrl failed due to an error',
				additionalContext: {
					innerError: e.innerError,
				},
			});
			return authUtils.constructAMLSecondaryTokenResponseToNative({
				id: requestParams.id,
				errorCode: AMLErrorCode.AML_START_URL_REQUEST_EXCEPTION,
				errorMessage: e.stack,
			});
		}
	}

	/// <summary>
	/// check if AML has a primary token for given service url
	/// </summary>
	async isLoggedOnToAMLService(id: string, serviceURL: string): Promise<boolean> {
		return await DsAuthPrimaryTokenServiceBase.isLoggedOnToAMLService(id, serviceURL);
	}
	/// <summary>
	/// common function to send destroy primary token request
	/// </summary>
	async sendDestroyTokenRequest(
		id: string,
		tokenObj: PrimaryTokenTableEntry,
		protectMode?: ProtectionMode
	): Promise<void> {
		try {
			Logger.getInstance().write(
				logConstants.INFO,
				id + ': Sending destroy token request for ' + tokenObj.serviceURL
			);
			let decryptedToken;
			if (Utils.getInstance().isBridgeFunctionSupported(ctxsNative, 'DecryptV2')) {
				const params: CryptoParams = {
					referenceUri: tokenObj.referenceUri,
					token: tokenObj.token,
					protectionMode: protectMode,
				};
				decryptedToken = await ctxsNative.DecryptV2(JSON.stringify(params));
			} else {
				decryptedToken = await ctxsNative.Decrypt(tokenObj.referenceUri, tokenObj.token);
			}
			await TokenServiceFactory.getPrimaryTokenServiceInstance(
				tokenObj.referenceUri
			).sendDestroyTokenRequest(
				decryptedToken,
				tokenObj.serviceURL,
				tokenObj.referenceUri,
				id
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Error while destroying Tokens. ' + e.message
			);
		}
	}
	/// <summary>
	/// send destroy token request for all the primary tokens
	/// </summary>
	async destroyTokens(id: string, protectMode?: ProtectionMode): Promise<void> {
		const tokenMgr = TokenManager.getInstance();
		const primaryTokens = await tokenMgr.getAllPrimaryTokens();
		try {
			if (primaryTokens && Array.isArray(primaryTokens)) {
				for (const tokenObj of primaryTokens) {
					await this.sendDestroyTokenRequest(id, tokenObj, protectMode);
				}
			}
		} catch (e) {
			//ignoring errors
			SentryLogger.getInstance().logError(e);
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Error while destroying Tokens. ' + e.message
			);
		}
	}

	/// <summary>
	/// send destroy token request for a store if user inactivity timer expired
	/// </summary>
	async destroyTokensForStore(id: string, referenceUri: string): Promise<void> {
		const tokenManager = TokenManager.getInstance();
		const tokenObj = await tokenManager.getPrimaryTokenForStore(referenceUri);
		if (tokenObj) {
			await this.sendDestroyTokenRequest(id, tokenObj);
		}
	}

	/**
	 * Used to makes a network request via native using bridge. Also the response returned is used to store cookie from header.
	 * @param method GET/PUT
	 * @param url
	 * @param headers
	 * @param body
	 * @param referenceUri
	 * @param id
	 * @returns response from native
	 * @memberof DSAuth
	 * NOTE: Native fetch returns a stringified Dictionary with key value pairs depending on the
	 * response. When the HTTP request fails , integer errorcode and description message as string is returned.
	 * In case of HTTP request success, dictionary with integer statuscode, string body and httpresponse headers are returned.
	 * Serialized JSON as string is passed to AML JS. When the response is parsed, unpacked into type which was sent.
	 */
	async fetch(
		method: string,
		url: string,
		headers: HeadersJson,
		body: string,
		referenceUri: string,
		allowCredUi = false,
		id?: string,
		includeClientCertificate?: boolean
	): Promise<HTTPFetchResponse> {
		let hdrs = headers;
		hdrs = await AuthUtils.getInstance().addCookiesToRequestHeader(
			headers,
			url,
			referenceUri
		);

		let requestDetails;
		if (id) {
			requestDetails = {
				method: method,
				url: url,
				headers: hdrs,
				body: body,
				id,
				includeClientCertificate,
			};
		} else {
			requestDetails = {
				method: method,
				url: url,
				headers: hdrs,
				body: body,
				includeClientCertificate,
			};
		}

		let response;
		try {
			if (
				Utils.getInstance().isBridgeFunctionSupported(ctxsNative, 'fetchWithCallback')
			) {
				response = await NativeBridgeWrapper.fetch(JSON.stringify(requestDetails));
			} else {
				response = await ctxsNative.fetch(JSON.stringify(requestDetails));
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Error during fetch bridge call. ' + e.message
			);
			throw new Error('Exception occur during fetch');
		}
		response = JSON.parse(response);

		if (Object.keys(response).length === 0) {
			throw new Error('Empty Response from Native');
		}
		if (
			response.errorCode &&
			response.errorCode >= HttpErrorCodes.ERROR_HTTP_FETCH_FAILURE
		) {
			throw new HttpRequestError(response.errorCode, response.errorDescription);
		}
		Logger.getInstance().write(
			logConstants.INFO,
			id +
				': HTTP Fetch: ' +
				Utils.getInstance().removeQueryStringAndHashFromUrl(url) +
				' Response Status Code: ' +
				response.statusCode +
				' AllowCredUi: ' +
				allowCredUi
		);
		await CookieManager.getInstance().storeCookiesFromHeader(
			response.headers,
			url,
			referenceUri
		);

		return response;
	}

	/// deletes primary token, secondary tokens and session cookies of a given store using referenceUri
	/// </summary>
	/// <param name="referenceUri">discovery url</param>
	async deleteTokensAndCookiesForStore(
		referenceUri: string,
		authDomain: string
	): Promise<void> {
		await TokenManager.getInstance().deleteTokensForStore(referenceUri, authDomain);
		await CookieManager.getInstance().deleteSessionCookiesForReferenceUri(referenceUri);
	}
}
